import { useState, useEffect } from "react";
import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { throwIfNotOk } from "@/lib/api-error";
import { isValidPhone } from "@/lib/phone";
import { isValidProfileVideoUrl } from "@/schemas/therapist";
import { BillingIdentityFields, type BillingIdentityValue } from "@/components/enrollment/BillingIdentityFields";
import { billingPayload, validateBilling } from "@/components/onboarding/onboarding-logic";
import {
  Check,
  CaretRight,
  CaretLeft,
  Phone,
  Briefcase,
  Camera,
  FileText,
  CircleNotch,
  WarningCircle,
  Sparkle,
  Question,
} from "@phosphor-icons/react";
import {
  StepPersonal,
  StepExpertise,
  StepProfile,
  StepDocuments,
  StepQuestionnaire,
  TherapistEnrollOtpGate,
  SuccessScreen,
  EnrollmentStatusScreen,
  EnrollmentStepProgress,
} from "@/components/therapist-enroll";
import type { EnrollFormData } from "@/components/therapist-enroll";
import type { EnrollmentQuestion } from "@/types/enrollment-questionnaire";

export const Route = createFileRoute("/therapist-enroll")({
  component: TherapistEnrollPage,
});

const ALL_STEPS = [
  {
    id: 0,
    label: "Kontakt",
    icon: Phone,
    heading: "Lični podaci",
    desc: "Kontakt informacije i podaci o licenci za praksu.",
    sideTitle: "Prvi korak",
    sideDesc:
      "Vaši podaci su sigurni. Koristimo ih isključivo za verifikaciju vašeg profesionalnog statusa.",
  },
  {
    id: 1,
    label: "Stručnost",
    icon: Briefcase,
    heading: "Stručnost i biografija",
    desc: "Odaberite područja specijalizacije i opišite svoj pristup terapiji.",
    sideTitle: "Vaša ekspertiza",
    sideDesc:
      "Klijenti biraju terapeute na osnovu specijalizacija. Detaljna biografija povećava vaše šanse za uspjeh.",
  },
  {
    id: 2,
    label: "Profil",
    icon: Camera,
    heading: "Profilna fotografija",
    desc: "Profesionalna fotografija pomaže klijentima da vas upoznaju.",
    sideTitle: "Prvi utisak",
    sideDesc:
      "Terapeuti sa profesionalnom fotografijom dobijaju do 3x više zahtjeva za sesije.",
  },
  {
    id: 3,
    label: "Upitnik",
    icon: Question,
    heading: "Upitnik za prijavu",
    desc: "Vaši odgovori pomažu našem timu da bolje razumije vaš pristup.",
    sideTitle: "Vaša priča",
    sideDesc: "Iskreni odgovori pomažu nam da brže donesemo odluku o vašoj prijavi.",
  },
  {
    id: 4,
    label: "Dokumenti",
    icon: FileText,
    heading: "Dokumenti i verifikacija",
    desc: "Uploadujte certifikate, diplome i licence.",
    sideTitle: "Završni korak",
    sideDesc:
      "Nakon slanja prijave, naš tim će pregledati vaše dokumente i obavijestiti vas o odluci putem emaila.",
  },
] as const;

function TherapistEnrollPage() {
  const { isAuthenticated, user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: specsData } = useQuery({
    queryKey: ["specializations"],
    queryFn: async () => {
      const res = await fetch("/api/specializations");
      await throwIfNotOk(res, "Greška pri učitavanju specijalizacija");
      return res.json() as Promise<{
        data: { id: string; name: string; description: string | null; iconUrl: string | null }[];
      }>;
    },
  });
  const availableSpecs = specsData?.data ?? [];

  const { data: questionnaireData } = useQuery({
    queryKey: ["enrollment-questionnaire"],
    queryFn: async () => {
      const res = await fetch("/api/enrollment-questionnaire");
      await throwIfNotOk(res, "Greška pri učitavanju upitnika");
      return res.json() as Promise<{ data: { questions: EnrollmentQuestion[] } }>;
    },
  });

  const { data: enrollmentStatus, isLoading: enrollmentLoading } = useQuery({
    queryKey: ["my-enrollment-status"],
    queryFn: async () => {
      const res = await fetch("/api/therapists/my-enrollment-status");
      await throwIfNotOk(res, "Greška pri provjeri statusa prijave");
      return res.json() as Promise<{
        data:
          | { hasEnrollment: false }
          | {
              hasEnrollment: true;
              status: "pending" | "approved" | "rejected";
              submittedAt: string;
              rejectionReason: string | null;
            };
      }>;
    },
    enabled: isAuthenticated,
  });
  const questions = questionnaireData?.data.questions ?? [];
  const STEPS = questions.length > 0
    ? ALL_STEPS
    : ALL_STEPS.filter((s) => s.id !== 3);
  const lastStepIndex = STEPS.length - 1;

  const [step, setStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState<EnrollFormData>({
    fullName: "",
    phone: "",
    licenseNumber: "",
    payoutMethod: "invoice",
    sessionModality: "online",
    specializations: [],
    bio: "",
    profileImageUrl: "",
    profileVideoUrl: "",
    documents: [],
    agreeToTerms: false,
    agreeToContract: false,
    isVolunteer: false,
    languages: [],
    questionnaireAnswers: {},
  });

  const [billing, setBilling] = useState<BillingIdentityValue>({});

  const { data: subSettings } = useQuery({
    queryKey: ["public-subscription-prices"],
    queryFn: async () => {
      const res = await fetch("/api/public/subscription-prices");
      return res.ok ? ((await res.json()) as { priceSemiannual: number; priceAnnual: number; currency: string }) : null;
    },
  });

  // Pre-fill the full name from the account name (from sign-up), if any. The
  // therapist can correct it here — this becomes the canonical name on the user.
  useEffect(() => {
    if (user?.name) {
      setForm((prev) => (prev.fullName ? prev : { ...prev, fullName: user.name as string }));
    }
  }, [user]);

  useEffect(() => {
    if (submitSuccess) {
      const timeout = setTimeout(() => navigate({ to: "/" }), 5000);
      return () => clearTimeout(timeout);
    }
  }, [submitSuccess, navigate]);

  function updateField<K extends keyof EnrollFormData>(key: K, value: EnrollFormData[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setError(null);
  }

  function toggleSpecialization(spec: string) {
    setForm((prev) => ({
      ...prev,
      specializations: prev.specializations.includes(spec)
        ? prev.specializations.filter((s) => s !== spec)
        : [...prev.specializations, spec],
    }));
  }

  function canAdvance(): boolean {
    const currentId = STEPS[step]?.id;
    switch (currentId) {
      case 0:
        return (
          form.fullName.trim().length >= 2 &&
          isValidPhone(form.phone)
        );
      case 1:
        return form.specializations.length > 0 && form.bio.length >= 10;
      case 2:
        // Neispravan video link StepProfile prikazuje odmah ispod polja.
        return (
          form.profileImageUrl.length > 0 &&
          (!form.profileVideoUrl.trim() || isValidProfileVideoUrl(form.profileVideoUrl.trim()))
        );
      case 3:
        return questions.every((q) => (form.questionnaireAnswers[q.id] ?? "").trim().length > 0);
      case 4:
        return form.documents.length > 0 && form.agreeToTerms && form.agreeToContract;
      default:
        return false;
    }
  }

  function nextStep() {
    if (!canAdvance()) return;
    if (step < lastStepIndex) setStep(step + 1);
  }

  function prevStep() {
    if (step > 0) setStep(step - 1);
  }

  async function handleSubmit() {
    if (!canAdvance()) return;
    // Isto pravilo kao server (enrollTherapistSchema): podaci za isplatu i račun
    // za isplatu su obavezni za sve osim volontera.
    const billingError = form.isVolunteer ? null : validateBilling(form.payoutMethod, billing);
    if (billingError) {
      setError(billingError);
      return;
    }
    setError(null);
    setIsSubmitting(true);
    try {
      const res = await fetch("/api/therapists/enroll", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fullName: form.fullName.trim(),
          bio: form.bio,
          specializations: form.specializations,
          licenseNumber: form.licenseNumber,
          phone: form.phone.trim(),
          payoutMethod: form.payoutMethod,
          sessionModality: form.sessionModality,
          profileImageUrl: form.profileImageUrl,
          profileVideoUrl: form.profileVideoUrl.trim() || undefined,
          isVolunteer: form.isVolunteer,
          languages: form.languages,
          documents: form.documents,
          questionnaireAnswers: questions.length > 0 ? form.questionnaireAnswers : undefined,
          // Samo grana odabranog načina isplate i račun, trimovano, bez praznih polja.
          ...billingPayload(form.payoutMethod, billing),
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.error ?? "Greška pri slanju prijave.");
      }
      setSubmitSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Došlo je do greške.");
    } finally {
      setIsSubmitting(false);
    }
  }

  /* ── Loading ── */
  if (authLoading || (isAuthenticated && enrollmentLoading)) {
    return (
      <div className="flex min-h-[70vh] items-center justify-center">
        <CircleNotch className="size-8 animate-spin text-primary/40" />
      </div>
    );
  }

  /* ── Auth gate ── */
  if (!isAuthenticated) {
    return (
      <TherapistEnrollOtpGate
        onAuthenticated={() => {
          void queryClient.invalidateQueries({ queryKey: ["auth", "session"] });
        }}
      />
    );
  }

  /* ── Existing enrollment (pending or rejected) — block re-entry ── */
  if (
    enrollmentStatus?.data.hasEnrollment &&
    (enrollmentStatus.data.status === "pending" ||
      enrollmentStatus.data.status === "rejected")
  ) {
    return (
      <EnrollmentStatusScreen
        status={enrollmentStatus.data.status}
        submittedAt={enrollmentStatus.data.submittedAt}
        rejectionReason={enrollmentStatus.data.rejectionReason}
      />
    );
  }

  /* ── Already a therapist or admin ── */
  if (user?.role === "therapist" || user?.role === "admin") {
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center px-4 text-center">
        <div className="mx-auto max-w-md space-y-4">
          <div className="mx-auto flex size-16 items-center justify-center rounded-full bg-primary/10">
            <Check className="size-8 text-primary" />
          </div>
          <h1
            className="text-2xl font-bold text-foreground"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            {user.role === "therapist"
              ? "Već ste registrovani kao terapeut"
              : "Administratori ne mogu pristupiti prijavi"}
          </h1>
          <p className="text-sm text-muted-foreground">
            {user.role === "therapist"
              ? "Vaš terapeutski profil je već aktivan. Možete upravljati svojim profilom i sesijama putem kontrolne table."
              : "Kao administrator, ne možete se prijaviti kao terapeut."}
          </p>
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground transition-all hover:bg-primary/90"
          >
            Idi na kontrolnu tablu
            <CaretRight className="size-4" />
          </Link>
        </div>
      </div>
    );
  }

  /* ── Success ── */
  if (submitSuccess) {
    return <SuccessScreen />;
  }

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const currentStep = STEPS[step]!;

  /* ── Main form ── */
  return (
    <div className="min-h-screen bg-gradient-to-br from-muted/40 via-background to-primary/3">
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-12 lg:py-16">
        {/* Back link */}
        <Link
          to="/"
          className="mb-6 inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground sm:mb-8"
        >
          <CaretLeft className="size-4" />
          Početna
        </Link>

        <div className="mx-auto max-w-4xl">
          <div className="mb-7 lg:mb-10">
            <p className="text-xs font-semibold uppercase tracking-[0.15em] text-primary">
              Postani terapeut
            </p>
            <h1
              className="mt-2 text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              Pridružite se platformi kao terapeut
            </h1>
          </div>

          <EnrollmentStepProgress
            steps={STEPS}
            currentStepIndex={step}
            onSelectStep={setStep}
          />

          {/* ── Form panel ── */}
          <div className="min-w-0">
            {/* Step heading (mobile shows it, desktop also) */}
            <div className="mb-6">
              <h2
                className="text-xl font-bold text-foreground sm:text-2xl"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                {currentStep.heading}
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {currentStep.desc}
              </p>
            </div>

            {/* Error */}
            {error && (
              <div className="mb-5 flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                <WarningCircle className="mt-0.5 size-4 shrink-0" />
                {error}
              </div>
            )}

            {/* Step content */}
            <div className="rounded-2xl border bg-card p-5 shadow-sm sm:p-7">
              {STEPS[step]?.id === 0 && (
                <StepPersonal form={form} updateField={updateField} />
              )}
              {STEPS[step]?.id === 1 && (
                <StepExpertise
                  form={form}
                  updateField={updateField}
                  toggleSpecialization={toggleSpecialization}
                  specializations={availableSpecs}
                />
              )}
              {STEPS[step]?.id === 2 && (
                <StepProfile form={form} updateField={updateField} />
              )}
              {STEPS[step]?.id === 3 && (
                <StepQuestionnaire
                  questions={questions}
                  form={form}
                  updateField={updateField}
                />
              )}
              {STEPS[step]?.id === 4 && (
                <div className="space-y-6">
                  <StepDocuments form={form} updateField={updateField} />

                  {!form.isVolunteer && subSettings && (
                    <div className="rounded-lg border border-stone-200 bg-stone-50 p-4 text-sm">
                      <h4 className="font-semibold mb-1">Mjesečna naknada za korištenje platforme</h4>
                      <ul className="list-disc list-inside text-stone-700 space-y-0.5">
                        <li>Polugodišnja pretplata: <strong>{subSettings.priceSemiannual} {subSettings.currency}</strong></li>
                        <li>Godišnja pretplata: <strong>{subSettings.priceAnnual} {subSettings.currency}</strong></li>
                      </ul>
                      <p className="text-xs text-stone-500 mt-2">
                        Plaćanje se vrši nakon odobrenja prijave. Nakon odobrenja dobićete email sa linkom za uplatu.
                      </p>
                    </div>
                  )}

                  <BillingIdentityFields
                    payoutMethod={form.payoutMethod}
                    isVolunteer={form.isVolunteer}
                    value={billing}
                    onChange={setBilling}
                  />
                </div>
              )}
            </div>

            {/* Navigation */}
            <div className="mt-5 flex items-center justify-between">
              {step > 0 ? (
                <Button variant="outline" onClick={prevStep} size="lg">
                  <CaretLeft className="size-4" />
                  Nazad
                </Button>
              ) : (
                <div />
              )}

              {step < lastStepIndex ? (
                <Button
                  onClick={nextStep}
                  disabled={!canAdvance()}
                  size="lg"
                >
                  Dalje
                  <CaretRight className="size-4" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={isSubmitting || !canAdvance()}
                  size="lg"
                >
                  {isSubmitting ? (
                    <CircleNotch className="size-4 animate-spin" />
                  ) : (
                    <Check className="size-4" />
                  )}
                  {isSubmitting ? "Slanje prijave..." : "Pošalji prijavu"}
                </Button>
              )}
            </div>

            {/* Mobile side info */}
            <div className="mt-6 rounded-xl border bg-card p-4 lg:hidden">
              <div className="flex items-center gap-2 text-xs font-semibold text-primary">
                <Sparkle className="size-3.5" />
                {currentStep.sideTitle}
              </div>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                {currentStep.sideDesc}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
